<template>
  <div class="space-y-2 pt-1">
    <!-- Selector -->
    <ResourcePicker resourceType="dataset" :model-value="selectedUUID" @update:model-value="onSelect" />

    <!-- Open in library -->
    <button
      v-if="selectedUUID"
      class="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
      @click="$emit('open-dataset', selectedUUID)"
    >Open Dataset →</button>

    <!-- No dataset warning -->
    <div v-if="!selectedUUID" class="text-xs text-amber-400/90">
      No dataset linked — this widget won't be exported to the device.
    </div>

    <!-- Series preview -->
    <div v-if="selectedDataset">
      <div v-if="selectedDataset.series.length > 0" class="space-y-1 pl-1">
        <div
          v-for="(s, i) in selectedDataset.series"
          :key="i"
          class="flex items-center gap-2 text-xs text-slate-300"
        >
          <span
            class="inline-block w-3 h-3 rounded-sm border border-slate-600 flex-shrink-0"
            :style="{ background: s.color || '#4da6ff' }"
          ></span>
          <span class="flex-1 truncate">{{ s.label || 'Unnamed series ' + i }}</span>
          <span
            class="text-slate-500 truncate font-mono"
            style="max-width: 110px;"
            :title="s.entity"
          >{{ s.entity || '—' }}</span>
        </div>
      </div>
      <div v-else class="text-xs text-slate-500 pl-1">
        No series defined in this dataset.
      </div>
    </div>

    <!-- Empty library hint -->
    <div v-if="datasets.length === 0" class="text-xs text-slate-500">
      No datasets in library — create one in the Datasets tab.
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useDatasetStore } from '../../stores/datasetStore.js'
import ResourcePicker from '../resources/ResourcePicker.vue'

const props = defineProps({
  node: { type: Object, required: true },
})
defineEmits(['open-dataset'])

const store = useDatasetStore()
const datasets = computed(() => store.all)

const selectedUUID = ref(props.node.dataset ?? '')
const selectedDataset = computed(() =>
  selectedUUID.value ? store.getDataset(selectedUUID.value) : null
)

watch(() => props.node, (node) => {
  selectedUUID.value = node?.dataset ?? ''
}, { immediate: true })


function onSelect(uuid) {
  selectedUUID.value = uuid
  if (uuid) {
    props.node.dataset = uuid
    // Generic hook for any dataset-driven widget (Legend, future widgets) to
    // redraw immediately on link — plain property assignment isn't reactive.
    props.node.fire('dataset')
    const ds = store.getDataset(uuid)
    if (ds) {
      // Keep Chart's legacy synchronization, but do not write Chart-only
      // fields onto other dataset-capable widgets such as Legend or Gauge.
      if (ds.series.length > 0 && props.node.hasProperty?.('series')) {
        props.node.series = ds.series.map(s => s.color)
        props.node.fire('series')
      }
      const ptCount = Math.round((ds.history_hours * 3600) / ds.interval)
      if (ptCount > 0 && props.node.hasProperty?.('point_count')) {
        props.node.point_count = ptCount
        props.node.fire('point_count')
      }
    }
  } else {
    delete props.node.dataset
    props.node.fire('dataset')
  }
}
</script>
